/**
 * Plan of Record governance action labels — one vocabulary for two distinct
 * ceremonies that were both getting called "Authorize" or "Ratify" in
 * different places.
 *
 * Canon (docs/1-canon/01-terminology-and-glossary.md, "## Plan Narrative
 * (PoR narrative layer)", migration 678 / P24(a) / DEP-026): a
 * **cycle-linked** Plan of Record is locked in by that plan cycle's
 * `authorize_por` ceremony — "it cannot be ratified independently". A
 * **cycle-less, org-level** record keeps its own first-class `ratify_por`
 * ceremony. The two words name two different gates, not two names for the
 * same action — using them interchangeably is the defect this file closes
 * (PlanningGovernance/MobileGovernance were both saying "Authorize as POR",
 * which reads as a request for a governance object that doesn't exist by
 * that name).
 */

/** Cycle-linked plan version → Plan of Record. PlanningGovernance. */
export const AUTHORIZE_POR_LABEL = 'Authorize plan of record';
export const AUTHORIZING_POR_LABEL = 'Authorizing…';
/** The dialog's first line when the gate refuses — the fix comes before the rule (build contract §2.8). */
export const CANNOT_AUTHORIZE_YET_LABEL = 'This version can’t be authorized yet.';
/** A version newer than the plan of record is the only row that offers Authorize; the others say what they are. */
export const NEVER_AUTHORIZED_LABEL = 'Never authorized';
/** The engine overview's hand-off to the act that writes a version. */
export const REVIEW_AND_COMMIT_LABEL = 'Review and propose';
/** The summary's preflight, before Propose: what will stop authorization later. */
export const WILL_BLOCK_AUTHORIZATION_LABEL = 'will block authorization';

/** Cycle-less, org-level record. RatifyPorModal, PorDashboard. */
export const RATIFY_POR_LABEL = 'Ratify';
export const RATIFYING_POR_LABEL = 'Ratifying…';

/** Engine output → new plan version (Doc 104 Steps 7→8). CommitToPlanVersionModal, EngineSummary. */
export const COMMIT_TO_PLAN_VERSION_LABEL = 'Propose';

/** New version copying the plan of record's initiatives. PlanningGovernance, iOS. */
export const PROPOSE_REPLAN_LABEL = 'Propose replan';

/** New version cloning the plan of record's full structure for editing. PlanningCapacity, PlanningPlanDetail. */
export const START_DRAFT_VERSION_LABEL = 'Start a draft version';

/** Pre-click reason when a cycle has no plan of record to replan or clone (server: no_plan_of_record). */
export const NO_POR_TO_CHANGE_REASON =
  'This plan has no plan of record yet — authorize a version first, then propose changes to it.';

/**
 * The displacement rule (owner ruling #12, migration 769; ceremony 701). Set on
 * a cycle template; a plan cycle created over that cycle's work starts with it.
 * Templates settings, Place a commitment, Above / Below the Line.
 */
export const DISPLACEMENT_RULE_LABEL = 'Ask “what comes off?” when work is added above the line';
export const DISPLACEMENT_RULE_HELP =
  'Plans made from cycles that use this template start with this rule on: adding work above the line means naming what comes off, or accepting the overcommitment by name. Applied once, when the plan is created.';
export const DISPLACEMENT_RULE_CHIP = 'What comes off? rule';
/** Plain notice in the Place-a-commitment flow when the plan cycle has the rule on. */
export const DISPLACEMENT_RULE_PLACE_NOTICE =
  'This plan asks “what comes off?” when work is added above the line. Answer it before you place this commitment.';
export const DISPLACEMENT_ANSWER_LINK_LABEL = 'Answer “what comes off?”';
/** Already answered for this commitment in this plan cycle. */
export const DISPLACEMENT_ALREADY_ANSWERED = '“What comes off?” is already answered for this commitment.';

/** Run History — go back to an earlier run's results (owner ruling #10, migration 769). */
export const MAKE_RUN_ACTIVE_LABEL = 'Make this run active';
